import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState, createRef } from 'react';

import { BrochurePagination, Meta } from '$partials/global';
import { Dispatcher } from '$partials/modules';
import { BrandLogo, CloseIcon, DownloadIcon, PrintIcon } from '$partials/icon';
import { formatNumber, formatPrice, formatRentFrequency, formatRoomRange } from '$utilities/format';
import { Button } from '$partials/ui';

const renderInfoCluster = (type, page) => {
    switch(type){
        case 'Share of freehold':
        case 'Leasehold':
            return (
                <div
                    className='absolute bottom-0 mb11 print-container'>
                    <div className='flex flex-wrap border'>
                        <div className='print-col-2 flex flex-wrap border-right'>
                            <div className='width-100 pt3 pl5 pb5 border-bottom'>
                                <p className='print-type-10px pt2 pb2'>
                                Bedrooms
                                </p>
                                <h3 className='print-type-22px'>
                                    {formatRoomRange(page?.bedrooms, page?.bedroomsMax)}
                                </h3>
                            </div>
                            <div className='pt3 pl5 pb5'>
                                <p className='print-type-10px pt2 pb2'>
                                Bathrooms
                                </p>
                                <h3 className='print-type-22px'>
                                    {formatRoomRange(page?.bathrooms, page?.bathroomsMax)}
                                </h3>
                            </div>
                        </div>
                        <div className='print-col-2 flex pt3 pl5 pb5 border-right'>
                            <div>
                                <p className='print-type-10px pt2 pb2'>
                                Internal
                                </p>
                                <div className='block'>
                                    <div className='pt5 pb2 flex items-baseline'>
                                        <h3 className='print-type-27px'>
                                            {page?.internalAreaFeet ? formatNumber(page?.internalAreaFeet) : '—'}
                                        </h3>
                                        {page?.internalAreaFeet ? <p className='print-type-10px ml2'>
                                        sq ft
                                        </p> : null }
                                    </div>
                                    <div className='pt5 flex items-baseline'>
                                        <h3 className='print-type-27px'>
                                            {page?.internalAreaMetres ? formatNumber(page?.internalAreaMetres) : '—'}
                                        </h3>
                                        {page?.internalAreaMetres ? <p className='print-type-10px ml2'>
                                        sqm
                                        </p> : null }
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='print-col-2 flex flex-wrap border-right'>
                            <div className='width-100 pt3 pl5 pb5 border-bottom'>
                                <p className='print-type-10px pt2 pb2'>
                                Outdoor
                                </p>
                                <div className='flex items-baseline'>
                                    <h3 className='print-type-22px'>
                                        {page?.outdoorAreaFeet ? formatNumber(page?.outdoorAreaFeet) : '—'}
                                    </h3>
                                    {page?.outdoorAreaFeet ? <p className='print-type-10px ml2'>
                                    sq ft
                                    </p> : null}
                                </div>
                            </div>
                            <div className='pt3 pl5 pb5'>
                                <p className='print-type-10px pt2 pb2'>
                                EPC
                                </p>
                                <h3 className='print-type-22px'>
                                    {page?.epcRating || '—'}
                                </h3>
                            </div>
                        </div>
                        <div className='print-col-2 flex flex-wrap'>
                            <div className='width-100 pt3 pl5 pb5 border-bottom'>
                                <p className='print-type-10px pt2 pb2'>
                                Council
                                </p>
                                <div className='flex items-center'>
                                    <h3 className='print-type-22px inline'>
                                        {page.localAuthority || '—'}
                                    </h3>
                                </div>
                            </div>
                            <div className='pt3 pl5 pb5'>
                                <p className='print-type-10px pt2 pb2'>
                                Council Tax
                                </p>
                                <h3 className='print-type-22px'>
                                    {page.councilTaxBand || '—'}
                                </h3>
                            </div>
                        </div>
                    </div>
                    <div className='flex flex-wrap border-right border-left'>
                        <div className='print-col-2 flex flex-wrap border-right'>
                            <div className='width-100 pt3 pl5 pb5 border-bottom'>
                                <p className='print-type-10px pt2 pb2'>Tenure</p>
                                <h3 className='print-type-22px'>{page?.tenure || '—'}</h3>
                            </div>
                        </div>
                        <div className='print-col-2 flex flex-wrap border-right'>
                            <div className='width-100 pt3 pl5 pb5 border-bottom'>
                                <p className='print-type-10px pt2 pb2'>Lease Length</p>
                                <span className='inline-flex items-baseline'>
                                    <h3 className='print-type-22px'>{page?.leaseLength || '—'}</h3>
                                    {page?.leaseLength ? (
                                        <p className='print-type-10px ml2'>years</p>
                                    ) : null}
                                </span>
                            </div>
                        </div>
                        <div className='print-col-2 flex flex-wrap border-right'>
                            <div className='width-100 pt3 pl5 pb5 border-bottom'>
                                <p className='print-type-10px pt2 pb2'>Ground Rent</p>
                                <span className='inline-flex items-baseline'>
                                    <h3 className='print-type-22px'>{page?.groundRent ? formatPrice(page?.groundRent) : '—'}</h3>
                                    {page?.groundRent ? (
                                        <p className='print-type-10px ml2'>p/y</p>
                                    ) : null}
                                </span>
                            </div>
                        </div>
                        <div className='print-col-2 flex flex-wrap'>
                            <div className='width-100 pt3 pl5 pb5 border-bottom'>
                                <p className='print-type-10px pt2 pb2'>Service Charge</p>
                                <span className='inline-flex items-baseline'>
                                    <h3 className='print-type-22px'>{page?.serviceCharge ? formatPrice(page?.serviceCharge) : '—'}</h3>
                                    {page?.serviceCharge ? (
                                        <p className='print-type-10px ml2'>p/y</p>
                                    ) : null}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            );
        case 'Rental':
            return (
                <div className='absolute bottom-0 width-100 mb11 print-container'>
                    <div className='flex flex-wrap border'>
                        <div className='print-col-2 flex flex-wrap border-right'>
                            <div className='width-100 pt3 pl5 pb5 border-bottom'>
                                <p className='print-type-10px md-pt2 pb2'>
                                Bedrooms
                                </p>
                                <h3 className='print-type-22px'>
                                    {formatRoomRange(page?.bedrooms, page?.bedroomsMax)}
                                </h3>
                            </div>
                            <div className='pt3 pl5 pb5'>
                                <p className='print-type-10px md-pt2 pb2'>
                                Bathrooms
                                </p>
                                <h3 className='print-type-22px'>
                                    {formatRoomRange(page?.bathrooms, page?.bathroomsMax)}
                                </h3>
                            </div>
                        </div>
                        <div className='print-col-2 flex pt3 pl5 pb5 border-right'>
                            <div>
                                <p className='print-type-10px md-pt2 pb2'>Internal</p>
                                <div className='block'>
                                    <div className='pt5 pb2 flex items-baseline'>
                                        <h3 className='print-type-27px'>
                                            {page?.internalAreaFeet ? formatNumber(page?.internalAreaFeet) : '—'}
                                        </h3>
                                        {page?.internalAreaFeet ? <p className='print-type-10px ml2'>
                                        sq ft
                                        </p> : null }
                                    </div>
                                    <div className='pt5 flex items-baseline'>
                                        <h3 className='print-type-27px'>
                                            {page?.internalAreaMetres ? formatNumber(page?.internalAreaMetres) : '—'}
                                        </h3>
                                        {page?.internalAreaMetres ? <p className='print-type-10px ml2'>
                                        sqm
                                        </p> : null}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='print-col-2 flex flex-wrap border-right'>
                            <div className='width-100 pt3 pl5 pb5 border-bottom'>
                                <p className='print-type-10px pt2 pb2'>
                                Outdoor
                                </p>
                                <div className='flex items-baseline'>
                                    <h3 className='print-type-22px'>
                                        {page?.outdoorAreaFeet ? formatNumber(page?.outdoorAreaFeet) : '—'}
                                    </h3>
                                    {page?.outdoorAreaFeet ? <p className='print-type-10px ml2'>
                                    sq ft
                                    </p> : null }
                                </div>
                            </div>
                            <div className='pt3 pl5 pb5'>
                                <p className='print-type-10px pt2 pb2'>
                                EPC
                                </p>
                                <h3 className='print-type-22px'>
                                    {page?.epcRating || '—'}
                                </h3>
                            </div>
                        </div>
                        <div className='print-col-2 flex flex-wrap'>
                            <div className='width-100 pt3 pl5 pb5 border-bottom'>
                                <p className='print-type-10px pt2 pb2'>
                                Furnished Status
                                </p>
                                <div className='flex items-center'>
                                    <h3 className='print-type-22px inline'>
                                        {page.furnishedStatus === 'Furnished / Part Furnished / Unfurnished' ? 'Multiple Options' : page.furnishedStatus || '—'}
                                    </h3>
                                </div>
                            </div>
                            <div className='pt3 pl5 pb5'>
                                <p className='print-type-10px pt2 pb2'>
                                Council Tax
                                </p>
                                <h3 className='print-type-22px'>
                                    {page?.localAuthority ? page.localAuthority : ''}
                                    {page?.localAuthority && page?.councilTaxBand ? ', ' : ''}
                                    {page?.councilTaxBand ? page.councilTaxBand : ''}
                                    {!page?.councilTaxBand && !page.localAuthority ? '–' : ''}
                                </h3>
                            </div>
                        </div>
                    </div>
                </div>
            );
        default:
            return (
                <div
                    className='absolute bottom-0 print-container mb11'
                    style={{width: 'calc(100% - var(--space-13))'}}>
                    <div className='flex flex-wrap border'>
                        <div className='print-col-2 flex flex-wrap border-right'>
                            <div className='width-100 pt3 pl5 pb5 border-bottom'>
                                <p className='print-type-10px md-pt2 pb2'>
                                Bedrooms
                                </p>
                                <h3 className='print-type-22px'>
                                    {formatRoomRange(page?.bedrooms, page?.bedroomsMax)}
                                </h3>
                            </div>
                            <div className='pt3 pl5 pb5'>
                                <p className='print-type-10px md-pt2 pb2'>
                                Bathrooms
                                </p>
                                <h3 className='print-type-22px'>
                                    {formatRoomRange(page?.bathrooms, page?.bathroomsMax)}
                                </h3>
                            </div>
                        </div>
                        <div className='print-col-2 flex pt3 pl5 pb5 border-right'>
                            <div>
                                <p className='print-type-10px md-pt2 pb2'>
                                Internal</p>
                                <div className='block'>
                                    <div className='pt5 pb2 flex items-baseline'>
                                        <h3 className='print-type-27px'>
                                            {page?.internalAreaFeet ? formatNumber(page?.internalAreaFeet) : '—'}
                                        </h3>
                                        {page?.internalAreaFeet ? <p className='print-type-10px ml2'>
                                        sq ft
                                        </p> : null}
                                    </div>
                                    <div className='pt5 flex items-baseline'>
                                        <h3 className='print-type-27px'>
                                            {page?.internalAreaMetres ? formatNumber(page?.internalAreaMetres) : '—'}
                                        </h3>
                                        {page?.internalAreaMetres ? <p className='print-type-10px ml2'>
                                        sqm
                                        </p> : null }
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='print-col-2 flex flex-wrap border-right'>
                            <div className='width-100 pt3 pl5 pb5 border-bottom'>
                                <p className='print-type-10px pt2 pb2'>
                                Outdoor
                                </p>
                                <div className='flex items-baseline'>
                                    <h3 className='print-type-22px'>
                                        {page?.outdoorAreaFeet ? formatNumber(page?.outdoorAreaFeet) : '—'}
                                    </h3>
                                    {page?.outdoorAreaFeet ? <p className='print-type-10px ml2'>
                                    sq ft
                                    </p> : null }
                                </div>
                            </div>
                            <div className='pt3 pl5 pb5'>
                                <p className='print-type-10px pt2 pb2'>
                                EPC</p>
                                <h3 className='print-type-22px'>
                                    {page?.epcRating || '—'}
                                </h3>
                            </div>
                        </div>
                        <div className='print-col-2 flex flex-wrap'>
                            <div className='width-100 pt3 pl5 pb5 border-bottom'>
                                <p className='print-type-10px pt2 pb2'>
                                Council Tax
                                </p>
                                <div className='flex items-center'>
                                    <h3 className='print-type-22px inline'>
                                        {page?.localAuthority ? page.localAuthority : ''}
                                        {page?.localAuthority && page?.councilTaxBand ? ', ' : ''}
                                        {page?.councilTaxBand ? page.councilTaxBand : ''}
                                        {!page?.councilTaxBand && !page.localAuthority ? '–' : ''}
                                    </h3>
                                </div>
                            </div>
                            <div className='pt3 pl5 pb5'>
                                <p className='print-type-10px pt2 pb2'>
                                Tenure</p>
                                <h3 className='print-type-22px'>
                                    {page?.tenure || '—'}</h3>
                            </div>
                        </div>
                    </div>
                </div>
            );
    }
};

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const Brochure = (props) => {
    const page = props.data?.page;
    const footer = props.data.footer;

    const clusterInfoPageRef = useRef(null);
    const floorplanPageRef = useRef(null);

    const [ isPrinting, setIsPrinting ] = useState(false);
    const elRefs = useRef([]);

    useEffect(() => {
        if (isPrinting) {
            window.print();
        }
        window.onafterprint = function() {
            setIsPrinting(false);
        };
    }, [isPrinting]);

    useEffect(() => {
        window.onbeforeprint = function() {
            setIsPrinting(true);
        };
    }, []);

    return (
        <>
            <Meta
                description={page.searchDescription || page.listingText}
                domain={props.domain}
                image={page.listingMedia?.[0]?.image}
                noindex={true}
                page={props.data?.page}
                title='Russell Simpson' />

            <div className={`sm-bg-grey ${isPrinting ? 'bg-white' : 'bg-grey'}`}>
                <div className={`z1 fixed flex mr6 right-0 top-0${isPrinting ? ' hide' : ''}`}>
                    <Button
                        download
                        aria-label='Download'
                        as = 'a'
                        className = 'bg-white p3 mt6 mr3 items-center justify-center'
                        href={`/brochure-pdf/${page.url?.replace('/', '')?.replaceAll('/', '-')}-${page.revisionId}.pdf`}
                        look = 'button'
                        rel='noopener noreferrer'
                        renderIcon = {<DownloadIcon/>}
                        style={{
                            width: '53px',
                            height:'53px'
                        }}
                        target='_blank'
                        type='button'/>
                    <Button
                        aria-label='Print'
                        as = 'a'
                        className = 'bg-white p3 mt6 mr3 items-center'
                        href={`https://russellsimpson.co.uk/brochure-pdf/${page.url.replace('/', '')?.replaceAll('/', '-')}-${page.revisionId}.pdf`}
                        look = 'button'
                        rel='noopener noreferrer'
                        renderIcon = {
                            <PrintIcon
                                height={19}
                                width={33}/>}
                        target='_blank'
                        type='button'/>
                    <Button
                        aria-label='Close'
                        as = 'a'
                        className = 'form-field-reset bg-white py2 px3 md-px5 md-py4 mt6 items-center type-style-5 uppercase'
                        href={page.url}
                        look = 'button'
                        renderIcon = {<CloseIcon className='ml3'/>}
                        type='button'>
                        Close
                    </Button>
                </div>
                {/* Cover */}
                <div className='print-page cover-page bg-white px8'>
                    <div className='absolute bottom-0 mb11 mr8'>
                        <div className='flex'>
                            <div
                                className='print-col-5 flex-shrink-0'
                                style={{transform: 'translateX(-50px)'}}>
                                <img src={page.brochureHeroImage?.src} />
                            </div>
                            <div className='print-col-3 flex flex-col my-auto'>
                                <BrandLogo className='print-logo mb6'/>
                                <h3 className='print-type-22px mb3'>
                                    {page.heading}
                                </h3>
                                <p className='print-type-10px'>
                                    {page.propertyName}
                                </p>
                                <p className='print-type-10px'>
                                    {page.area}{' '}{page.outwardCode}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/*Info cluster */}
                <div
                    className='print-page bg-white px8 pt9 pb11'
                    ref={clusterInfoPageRef}>
                    <div className={`flex ${page.__typename === 'PropertyPage' && page.publicSellingStatus !== 'Sold' && page?.qualifier === 'offersInExcess' ? 'items-end' : ''}`}>
                        <h3 className='print-col-4 print-type-27px'>{page.propertyName}</h3>
                        <div className='print-col-1'/>
                        <div className='print-col-3'>
                            <div className='flex flex-col items-baseline'>
                                {page.__typename === 'PropertyPage' && page.publicSellingStatus !== 'Sold' && page?.qualifier === 'offersInExcess' && (
                                    <p className='type-style-5'>
                                        Offers IEO
                                    </p>
                                )}
                                {page.__typename === 'PropertyPage' && page.publicSellingStatus !== 'Sold' && page?.qualifier === 'offersOver' && (
                                    <p className='type-style-5'>
                                        Offers Over
                                    </p>
                                )}
                                {page.__typename === 'PropertyPage' && page.publicSellingStatus !== 'Sold' && page?.qualifier === 'guidePrice' && (
                                    <p className='type-style-5'>
                                        Guide Price
                                    </p>
                                )}
                                <div className='flex items-baseline'>
                                    <h3 className='print-type-27px'>
                                        {page.__typename === 'PropertyPage' && page.publicSellingStatus === 'Sold' && 'Sold'}
                                        {page.__typename === 'PropertyPage' && page.publicSellingStatus !== 'Sold' && formatPrice(page.priceGbp)}
                                        {page.__typename === 'RentalPropertyPage' && page.publicSellingStatus === 'Rented' && 'Let'}
                                        {page.__typename === 'RentalPropertyPage' && page.publicSellingStatus !== 'Rented' && formatPrice(page.rentGbp)}
                                    </h3>
                                    {page.__typename === 'RentalPropertyPage' && page.publicSellingStatus !== 'Rented' && (
                                        <p className='print-type-10px pl3'>
                                            {' '}{formatRentFrequency(page.rentFrequency)}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    {renderInfoCluster(page.__typename === 'RentalPropertyPage' ? 'Rental' : page.tenure, page)}

                    <BrochurePagination
                        propertyName={page.propertyName}
                        ref={clusterInfoPageRef} />
                </div>

                {page.modules.map((module, idx) => {
                    return (
                        <Dispatcher
                            index={idx}
                            key={`brochure-module-${idx}`}
                            module={module}
                            moduleNextType={page.modules?.[idx + 1]?.__typename}
                            modulePreviousType={page.modules?.[idx - 1]?.__typename}
                            namespace={`brochure-module-${idx}`}
                            pageType={'brochure'}
                            propertyName={page.propertyName}
                            {...props}/>
                    );})}

                {/* Floorplan */}
                {page.brochureFloorplanImage && (
                    <div
                        className='print-page bg-white pt7 px8 pb9'
                        ref={floorplanPageRef}>
                        <img
                            className='height-100 width-100 object-contain'
                            src={page.brochureFloorplanImage?.src}/>
                        <BrochurePagination
                            propertyName={page.propertyName}
                            ref={floorplanPageRef} />
                    </div>
                )}


                {page.brochureFloorplanImages.length > 0 && page.brochureFloorplanImages.map((floorplan, i) => {
                    return (
                        <div
                            className='print-page bg-white pt7 px8 pb9'
                            key={`id-${floorplan?.image.src}`}
                            ref={(el) => { elRefs.current[i] = el; }}>
                            <img
                                className='height-100 width-100 object-contain'
                                src={floorplan?.image.src}/>
                            <BrochurePagination
                                propertyName={page.propertyName}
                                ref={elRefs.current[i]} />
                        </div>
                    );
                })}

                {/* Terms */}
                <div className='print-page bg-peach'>
                    <div className='px8 pt9 flex'>
                        <div className='print-col-2'>
                            <BrandLogo className='print-logo'/>
                        </div>
                        <div className='print-col-2 flex flex-col' />
                        <div className='print-col-1'/>
                        <div className='print-col-2 flex flex-wrap'>
                            {page?.contactForm?.brochureContact?.length > 0 ?
                                page.contactForm.brochureContact.map((item) => {
                                    return (
                                        <div
                                            className='border-top contact-detail-item'
                                            key={item?.id}>
                                            {/* Title */}
                                            <h3 className='print-type-22px mb4 mt3'>
                                                {item.title}
                                            </h3>
                                            {/* Body */}
                                            <div
                                                className='rich-text brochure print-type-12px'
                                                dangerouslySetInnerHTML={{ __html: item.address}}/>
                                        </div>
                                    );
                                }) : footer?.contactDetails?.map((item, idx) => {
                                    if (idx === 0) {
                                        return null;
                                    }
                                    return (
                                        <div
                                            className='border-top contact-detail-item'
                                            key={item?.id}>
                                            {/* Title */}
                                            <h3 className='print-type-22px mb4 mt3'>
                                                {item.title}
                                            </h3>
                                            {/* Body */}
                                            <div
                                                className='rich-text brochure print-type-12px'
                                                dangerouslySetInnerHTML={{ __html: item.body}}/>
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                    <div className='absolute print-container bottom-0 mb6 mx8 flex'>
                        <div className='print-col-2'/>
                        <div className='print-col-5'>
                            <p className='print-type-6px opacity-0-4'>
                            In accordance with the Property Mis-Description Act 1991, the following details have been prepared in good faith, and are not intended to constitute part of an offer or contract. Any information contained herein (whether in the text, plans or photographs) is given in good faith but should not be relied upon as being a statement of representation of fact. Nothing in these Particulars shall be deemed to be a statement that the property is in good condition or otherwise nor that any services or facilities are in good working order. Any measurements of distances referred to herein are approximate only. October 2021
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    data: PropTypes.shape({
        page: PropTypes.shape({
            __typename: PropTypes.string,
            title: PropTypes.string,
            listingImage: PropTypes.object,
            listingText: PropTypes.string,
            searchDescription: PropTypes.string
        }),
        navigation: PropTypes.object,
        footer: PropTypes.object,
    }),
    domain: PropTypes.string,
};

Brochure.propTypes = propTypes;

export default Brochure;
